import _jsx from "@babel/runtime/helpers/jsx";
/**
 *  This "Pricing" Component includes the front end logic and display for inventory pricing
 *
 **/
import PropTypes from 'prop-types';
import React from 'react';
import clsx from 'clsx';
import { Text, Button, InfoIcon } from 'reaxl';
import { formatCurrency, formatNumber } from '@atc/string-fns';
const renderMSRPWithLink = _ref => {
  var _span;
  let {
    context,
    formattedPrice,
    isFirstPrice,
    onClick,
    salePriceOnly = false,
    showCompact = false,
    showDeal = false,
    showDefault = false,
    showMSRPLabel = false,
    showPriceAndLink = true,
    showPayment
  } = _ref;
  const shouldDisplayMSRP = showDefault && (showMSRPLabel || salePriceOnly) || !showDefault;
  const showMSRPLabelPriceDifference = showPriceAndLink && !showDefault || showDeal && !isFirstPrice;
  const prefixClasses = clsx({
    'text-size-100 text-subdued display-block': !isFirstPrice && !showPayment,
    'padding-bottom-3': !['details', 'withDeal', 'withPayment', 'withDealIndicator'].includes(context),
    'padding-bottom-1': context === 'details',
    'display-flex align-items-center': !showMSRPLabelPriceDifference && showPayment
  }, 'text-normal', 'second-price');
  const msrpClassNames = clsx({
    'display-block': !showDeal,
    'padding-left-1': showDeal,
    'text-size-100': !showPayment,
    'text-size-200 margin-top-1': showPayment,
    'text-subdued': !showCompact
  });
  const viewPricingClassNames = clsx({
    'text-size-100': !showDefault,
    'text-size-200': showDefault
  }, 'display-block');
  const getMsrpPriceTextWeight = () => {
    if (showMSRPLabelPriceDifference) {
      return undefined;
    }
    return context === 'withDealIndicator' ? 'ultra-bold' : 'bold';
  };
  const renderMsrpInfo = () => shouldDisplayMSRP && /*#__PURE__*/_jsx(React.Fragment, {}, "msrp", showMSRPLabelPriceDifference && 'MSRP ', /*#__PURE__*/_jsx(Text, {
    weight: getMsrpPriceTextWeight()
  }, void 0, formattedPrice), !showPriceAndLink && !showDefault && (_span || (_span = /*#__PURE__*/_jsx("span", {
    className: msrpClassNames
  }, void 0, "MSRP\xA0"))));
  return /*#__PURE__*/_jsx("span", {
    className: prefixClasses
  }, void 0, renderMsrpInfo(), showPriceAndLink && !['details', 'withDeal', 'withPayment', 'withDealIndicator'].includes(context) && /*#__PURE__*/_jsx(Button, {
    className: viewPricingClassNames,
    onClick: event => onClick(event),
    bsStyle: "text"
  }, void 0, "See Pricing Details"));
};

/**
 * data, uiContext, onClick
 */
const getPricingVariables = (_ref2, context, onClick, showCompact, showDeal, showDefault, showPayment) => {
  let {
    incentive,
    msrp,
    salePrice,
    noPriceLabel
  } = _ref2;
  let firstPrice;
  let secondPrice;
  let noPrice;
  let showPricingDetailsCTA = false;
  const salePriceValue = incentive || salePrice;
  const salePriceOnly = salePriceValue && !msrp;
  const msrpOnly = msrp && msrp !== 0 && !salePriceValue;
  const saleAndMSRPNotEqual = salePriceValue && msrp && salePriceValue !== msrp;
  const saleAndMSRPEqual = salePriceValue && msrp && salePriceValue === msrp;
  const priceDifference = salePriceValue && msrp && msrp - salePriceValue;
  const hasDeal = priceDifference > 0;
  const showMSRPLabel = msrpOnly || saleAndMSRPEqual;
  const style = showPayment || showCompact ? 'currency' : '';
  let priceClassName = clsx('text-bold', {
    'text-gray-base': !showDefault && !showCompact,
    'text-size-300 text-blue-darker text-link': showDefault && !showPayment,
    'text-size-400': showCompact,
    'text-size-500': ['listing', 'snapshot', 'withDealIndicator'].includes(context),
    'text-size-600': ['details', 'withDeal', 'withPayment'].includes(context)
  });
  if (salePriceOnly) {
    firstPrice = formatNumber({
      value: salePriceValue,
      style
    });
  } else if (showMSRPLabel) {
    firstPrice = formatNumber({
      value: msrp,
      style
    });
    showPricingDetailsCTA = true;
  } else if (saleAndMSRPNotEqual) {
    firstPrice = formatNumber({
      value: salePriceValue,
      style
    });
    secondPrice = formatCurrency(msrp);
    showPricingDetailsCTA = true;
  } else {
    firstPrice = noPriceLabel;
    priceClassName = showPayment ? 'text-size-400' : 'text-size-300';
    noPrice = true;
  }
  firstPrice = showPricingDetailsCTA && showMSRPLabel || showDefault && salePriceOnly ? renderMSRPWithLink({
    context,
    formattedPrice: firstPrice,
    isFirstPrice: true,
    onClick,
    showPriceAndLink: showDefault,
    salePriceOnly,
    showCompact,
    showDeal,
    showDefault,
    showMSRPLabel,
    showPayment
  }) : firstPrice;
  secondPrice = secondPrice && showPricingDetailsCTA && renderMSRPWithLink({
    context,
    formattedPrice: secondPrice,
    isFirstPrice: false,
    onClick,
    showCompact,
    showDeal,
    showDefault,
    showMSRPLabel,
    showPayment
  });
  secondPrice = ['default', 'listing', 'details', 'withDeal', 'withPayment', 'withDealIndicator'].includes(context) ? secondPrice : null;
  return {
    firstPrice,
    secondPrice,
    priceClassName,
    priceDifference: hasDeal && formatCurrency(priceDifference),
    noPrice
  };
};
function Pricing(_ref3) {
  var _React$Fragment;
  let {
    className,
    data = {},
    isPreorder = false,
    // TODO convert to uiContext
    onPricingDetailClick = () => {},
    orientation,
    uiContext = 'listing',
    walletCTA,
    showSavings,
    ...rest
  } = _ref3;
  let showDeal = uiContext === 'withDeal';
  let showDefault = uiContext === 'default';
  const showCompact = uiContext === 'compact';
  const showPayment = ['withPayment', 'withDealIndicator'].includes(uiContext);
  const showPriceDifference = !!showSavings || !!showDeal;
  if (showPayment || showCompact) {
    showDeal = true;
    showDefault = false;
  }
  const {
    firstPrice,
    secondPrice,
    priceClassName,
    priceDifference,
    noPrice
  } = getPricingVariables(data, uiContext, onPricingDetailClick, showCompact, showDeal, showDefault, showPayment);
  const rootClasses = clsx(priceClassName, className, {
    'display-flex align-items-center': showDeal || showPayment || showCompact
  });
  const msrpTooltip = 'MSRP excludes tax, title, license, delivery, processing & handling fees and optional equipment. Contact dealer for details.';
  const renderPriceDifference = () => {
    const savedPrice = showDeal && !!secondPrice && /*#__PURE__*/_jsx("div", {
      className: clsx('padding-left-3', {
        'display-block': !showPayment,
        'display-flex align-item-center': showPayment
      })
    }, void 0, /*#__PURE__*/_jsx(Text, {
      className: clsx('second-price', {
        'display-block': !showPayment,
        'margin-right-2': showPayment
      }),
      color: "subdued-lightest",
      size: 200,
      weight: "normal"
    }, void 0, secondPrice), showPriceDifference && !!priceDifference && /*#__PURE__*/_jsx(Text, {
      className: clsx({
        'display-block': !showPayment
      }),
      color: "success",
      size: 200,
      weight: "light"
    }, void 0, `Save ${priceDifference}`));
    return !!walletCTA || showDeal && !!secondPrice ? /*#__PURE__*/_jsx("span", {
      className: clsx({
        'display-flex flex-column': showPayment
      })
    }, void 0, savedPrice, !!walletCTA && !noPrice && (_React$Fragment || (_React$Fragment = /*#__PURE__*/_jsx(React.Fragment, {}, "wallet-cta", walletCTA)))) : null;
  };
  return /*#__PURE__*/React.createElement("div", Object.assign({
    "data-cmp": "pricing",
    className: rootClasses
  }, rest), /*#__PURE__*/_jsx("span", {
    className: clsx({
      'first-price': !showDeal && !noPrice && !showPayment && !showCompact,
      'first-price first-price-lg': showDeal && !noPrice && !showPayment && !showCompact,
      'text-size-700': uiContext === 'withDeal' && !noPrice,
      'text-size-600': showPayment && !noPrice && uiContext !== 'withDealIndicator',
      'padding-bottom-1': noPrice && showPayment
    }, 'text-ultra-bold'),
    "data-cmp": "firstPrice"
  }, void 0, firstPrice), !isPreorder && /*#__PURE__*/React.createElement(React.Fragment, null, !showDeal && secondPrice, renderPriceDifference()), isPreorder && !showDeal && /*#__PURE__*/_jsx("span", {
    className: "text-size-200 text-subdued-lightest display-block padding-bottom-3 text-normal second-price"
  }, void 0, /*#__PURE__*/_jsx(InfoIcon, {
    popoverId: "msrp-more-info-popover",
    placement: "top",
    infoText: msrpTooltip
  }), "Starting MSRP", ' ', /*#__PURE__*/_jsx("span", {
    className: clsx({
      'display-block': orientation === 'landscape'
    })
  }, void 0, "before options")));
}
export default Pricing;